import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { QUALITY_SERVICE_API_URL } from '../config';
import { formatLocalDate } from '../utils/dateFormat';

async function fetchProductionIncidentsByTeamTime(breakdown, queryString) {
  const url = `${QUALITY_SERVICE_API_URL}/hub/production-incidents/by-team-time?breakdown=${breakdown}&${queryString}`;
  try {
    const response = await api.get(url);
    return response.data;
  } catch (err) {
    console.error(`[useProductionIncidentsByTeamTime] ${breakdown} failed:`, {
      status: err.response?.status,
      message: err.message,
    });
    throw err;
  }
}

function useProductionIncidentsByTeamTime(breakdown, filters, dateRange) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Build query string
  const params = new URLSearchParams();
  ['domain', 'group', 'unit'].forEach(key => {
    (filters?.[key] || []).forEach(value => params.append(key, value));
  });

  if (dateRange?.startDate) params.set('from', formatLocalDate(dateRange.startDate));
  if (dateRange?.endDate) params.set('to', formatLocalDate(dateRange.endDate));

  const queryString = params.toString();

  const fetchBreakdown = useCallback(async (isCancelled = () => false) => {
    if (!breakdown || !dateRange?.startDate || !dateRange?.endDate) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);
    try {
      const rows = await fetchProductionIncidentsByTeamTime(breakdown, queryString);
      if (!isCancelled()) setData(rows);
    } catch (err) {
      if (!isCancelled()) setError(err);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [breakdown, queryString, dateRange?.startDate, dateRange?.endDate]);

  useEffect(() => {
    let cancelled = false;
    fetchBreakdown(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchBreakdown]);

  return { data, loading, error, refetch: () => fetchBreakdown() };
}

export default useProductionIncidentsByTeamTime;
